import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import {
  createHseTiltak,
  fetchHseTiltakItem,
  updateHseTiltak,
} from "../../../../api/hse/tiltak";
import { fetchHseSettings } from "../../../../api/hse/settings";
import { HseTiltakForm } from "../../../../components/dashboard/hse/HseTiltakForm";
import {
  emptyTiltakForm,
  tiltakFromRecord,
} from "../../../../components/dashboard/hse/hseTiltakUtils";

export const HseKsTiltakEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === "new";
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (isNew) {
          const settings = await fetchHseSettings().catch(() => ({}));
          if (cancelled) return;
          setFormValues(
            emptyTiltakForm({
              dueDays: settings?.default_tiltak_due_days,
              responsible_user_id: settings?.responsible_user_id || "",
              requires_approval: settings?.tiltak_require_approval === true,
              hse_event_id: searchParams.get("hse_event_id") || "",
              hse_evaluation_id:
                searchParams.get("hse_evaluation_id") ||
                searchParams.get("evaluation_id") ||
                "",
              hse_document_id: searchParams.get("hse_document_id") || "",
              action_type: searchParams.get("action_type") || "generic",
              title: searchParams.get("title") || "",
            })
          );
          setLoading(false);
          return;
        }

        setLoading(true);
        const record = await fetchHseTiltakItem(id);
        if (!cancelled) setFormValues(tiltakFromRecord(record));
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, searchParams]);

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const saved = isNew
        ? await createHseTiltak(payload)
        : await updateHseTiltak(id, payload);
      toast.success(isNew ? t("hse.tiltak.created") : t("hse.tiltak.updated"));
      navigate(`/dashboard/hse-ks/tiltak/${saved.id || id}`);
    } catch (err) {
      toast.error(err.message || t("hse.tiltak.save_failed"));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <HsePageChrome
      title={isNew ? t("hse.tiltak.new") : t("hse.tiltak.edit")}
      subtitle={t("hse.tiltak.form_subtitle")}
      backTo={isNew ? "/dashboard/hse-ks/tiltak" : `/dashboard/hse-ks/tiltak/${id}`}
    >
      <div className="mx-auto mt-8 max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          {loading ? (
            <ResourceLoading />
          ) : error ? (
            <ResourceError errors={[t("hse.tiltak.load_failed")]} />
          ) : (
            <HseTiltakForm
              initialValues={formValues}
              loading={saving}
              onSubmit={handleSubmit}
              onCancel={() =>
                navigate(isNew ? "/dashboard/hse-ks/tiltak" : `/dashboard/hse-ks/tiltak/${id}`)
              }
              submitLabel={isNew ? t("hse.tiltak.create") : t("hse.tiltak.save")}
            />
          )}
        </div>
      </div>
    </HsePageChrome>
  );
};
